import React from 'react';
import { Autocomplete, TextField, CircularProgress, Box, AutocompleteProps, Button } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useGetTDSOptionsQuery } from '../Api/CompanyCodeTranslation.api';
import type { TDSOptions } from '../types';

interface TDSAutocompleteProps extends Omit<AutocompleteProps<TDSOptions, false, false, false>, 'options' | 'renderInput' | 'getOptionLabel' | 'isOptionEqualToValue' | 'onChange' | 'value' | 'onBlur'> {
  name?: string;
  label?: string;
  value?: string | TDSOptions | null;
  onChange?: (option: TDSOptions | null) => void;
  onBlur?: (event?: any) => void;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  required?: boolean;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
  codetype?: string;
}

const TDSAutocomplete: React.FC<TDSAutocompleteProps> = ({
  name,
  label = 'TDS Section',
  value,
  onChange,
  onBlur,
  error = false,
  helperText,
  placeholder = 'Select TDS Section',
  required = false,
  size = 'medium',
  variant = 'outlined',
  codetype = '1',
  ...autocompleteProps
}) => {
  const { data: tdsOptions = [], isLoading, isError, refetch } = useGetTDSOptionsQuery({ codetype });

  // Convert string value to option object for internal use
  const getOptionFromValue = (val: string | TDSOptions | null | undefined): TDSOptions | null => {
    if (!val) return null;
    if (typeof val === 'string') {
      return tdsOptions.find(option => option.value === val) || null;
    }
    return val;
  };

  const internalValue = getOptionFromValue(value);

  const handleChange = (event: any, newValue: TDSOptions | null) => {
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleBlur = (event: any) => {
    if (onBlur) {
      onBlur(event);
    }
  };

  const getOptionLabel = (option: TDSOptions) => {
    if (typeof option === 'string') return option;
    return `${option.label} (${option.percentage}%)`;
  };

  const isOptionEqualToValue = (option: TDSOptions, value: TDSOptions) => {
    return option.value === value.value;
  };

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: TDSOptions) => (
    <Box component="li" {...props} key={String(option.value)}>
      <Box>
        <Box sx={{ fontWeight: 'medium' }}>{option.label}</Box>
        <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
          Section: {option.value} | Percentage: {option.percentage}%
        </Box>
      </Box>
    </Box>
  );

  if (isError) {
    return (
      <Autocomplete
        options={[]}
        value={null}
        disabled={true}
        getOptionLabel={() => ''}
        isOptionEqualToValue={() => false}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            label={label}
            placeholder="Error loading TDS options"
            error={true}
            helperText="Failed to load TDS options"
            size={size}
            variant={variant}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => refetch()}
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  Retry
                </Button>
              ),
            }}
          />
        )}
        noOptionsText=""
        {...autocompleteProps}
      />
    );
  }

  return (
    <Autocomplete
      options={tdsOptions}
      value={internalValue}
      onChange={handleChange}
      onBlur={handleBlur}
      loading={isLoading}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          required={required}
          size={size}
          variant={variant}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText="No TDS options available"
      loadingText="Loading TDS options..."
      {...autocompleteProps}
    />
  );
};

export default TDSAutocomplete;
